import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/pl';
import {FaCircle, FaEdit, FaFileAlt, FaFolder, FaHome, FaUsers} from 'react-icons/fa';
import {AppCtx} from '../../../AppCtx';
import {ThemeContext} from '../../../ThemeContext';
import {ConnectionStatusEnum} from '../../../infrastructure/events/EventBridgeComponent';
import {getDeviceInfo, getRecentFiles} from '../../../infrastructure/storage/localStorage';
import {useSelectSong} from '../../navigation/components/Navigation';
import {useVersionInfo} from '../../../shared/services/VersionService';
import {SessionManager} from '../../../infrastructure/storage/SessionManager';
import {buildCatalog, catalogUrl, fileTitle, isSongbook} from './dashboardData';
import {useSongbookLibrary} from './useSongbookLibrary';
import './DashboardComponent.css';
import {SongLibraryTiles} from '../../song/library/SongLibraryPages';
import VersionStatus from './VersionStatus';
import {ChangelogTile} from '../../changelog/ChangelogPages';
import LegacyMigrationNotice from './LegacyMigrationNotice';

moment.locale('pl');

const DashboardComponent = () => {
  const {connectionStatus, connectedUsers, currentSessionId, deviceName, setDeviceName,
    selectedFile} = useContext(AppCtx);
  const version = useVersionInfo();
  const selectSong = useSelectSong();
  const [recentFiles, setRecentFiles] = useState([]);
  const {files, songsCount} = useSongbookLibrary(true);
  const [editingDevice, setEditingDevice] = useState(false);
  const [deviceDraft, setDeviceDraft] = useState('');

  useEffect(() => { setRecentFiles(getRecentFiles().filter(file => isSongbook(file.fileName))); }, [selectedFile]);

  const catalog = useMemo(() => buildCatalog(files), [files]);
  const recentPaths = new Set(recentFiles.map(file => file.fileName));
  const saveDevice = event => {
    event.preventDefault();
    if (deviceDraft.trim()) setDeviceName(deviceDraft.trim());
    setEditingDevice(false);
  };
  const connected = connectionStatus === ConnectionStatusEnum.WS_CONNECTED;
  const session = SessionManager.getCurrentSession();
  const deviceInfo = getDeviceInfo() || {};
  const deviceLabel = deviceName || String(deviceInfo.guid || '').substring(0, 8) || 'Urządzenie';

  return <div className="dashboard-home">
    <LegacyMigrationNotice/>
    <div className="dashboard-main-grid">
      <section className="dashboard-tile dashboard-tile-large">
        <div className="dashboard-tile-heading"><FaFileAlt/><h1>Ostatnio przeglądane</h1></div>
        <div className="dashboard-tile-subtitle">{recentFiles.length} ostatnich śpiewników</div>
        <div className="dashboard-recent-list">
          {recentFiles.slice(0, 10).map(file => <button type="button" className="dashboard-file-link" key={file.fileName}
            onClick={() => selectSong(file.fileName, file.songNumber)}>
            <span>{fileTitle(file.fileName)}</span><time>{moment(file.dateTime).fromNow()}</time>
          </button>)}
          {!recentFiles.length && <p className="dashboard-empty">Otwórz śpiewnik, aby pojawił się tutaj.</p>}
        </div>
        <Link className="dashboard-more" to="/app/recent">Pełna lista →</Link>
      </section>

      <section className="dashboard-tile dashboard-tile-large">
        <div className="dashboard-tile-heading"><FaFolder/><h1>Katalogi śpiewników</h1></div>
        <div className="dashboard-tile-subtitle">{catalog.folders.length} katalogów · {files.length} śpiewników · {songsCount ?? '…'} piosenek</div>
        <div className="dashboard-folder-list">
          {catalog.folders.slice(0, 5).map(folder => <Link key={folder.path} to={catalogUrl(folder.path)}>
            <FaFolder/><span>{folder.name}</span><small>{folder.allFiles.length}</small>
            {folder.allFiles.some(path => recentPaths.has(path)) && <span className="dashboard-recent-dot" title="Ostatnio otwierane"/>}
          </Link>)}
          {!catalog.folders.length && <p className="dashboard-empty">Brak katalogów w tej sesji.</p>}
        </div>
        <Link className="dashboard-more" to={catalogUrl('')}>Wszystkie katalogi →</Link>
      </section>

      <ChangelogTile/>
    </div>

    <SongLibraryTiles/>

    <footer className="dashboard-footer">
      <div className="dashboard-footer-item dashboard-footer-status"><span className="dashboard-footer-label">Status połączenia</span>
        <strong className={connected ? 'dashboard-connected' : 'dashboard-disconnected'}><FaCircle/> {connected ? 'Połączony' : 'Niepołączony'}</strong></div>
      <div className="dashboard-footer-item"><span className="dashboard-footer-label">Nazwa urządzenia</span>
        {editingDevice ? <form onSubmit={saveDevice}><input autoFocus value={deviceDraft} onChange={event => setDeviceDraft(event.target.value)} aria-label="Nazwa urządzenia"/><button type="submit">Zapisz</button></form>
          : <button type="button" onClick={() => {setDeviceDraft(deviceLabel); setEditingDevice(true);}}>{deviceLabel} <FaEdit/></button>}</div>
      <div className="dashboard-footer-item"><span className="dashboard-footer-label">Sesja</span><strong>{session?.name || currentSessionId?.substring(0, 8) || 'Brak aktywnej sesji'}</strong></div>
      <div className="dashboard-footer-item"><span className="dashboard-footer-label">Połączeni użytkownicy</span><strong><FaUsers/> {(connectedUsers || []).length}</strong></div>
      <div className="dashboard-footer-item dashboard-footer-version"><span className="dashboard-footer-label">Wersja i data kompilacji</span>
        <VersionStatus {...version}/>
        {version.versionInfo?.buildTime && <time className="dashboard-version-date" dateTime={version.versionInfo.buildTime}>
          {new Date(version.versionInfo.buildTime).toLocaleDateString('pl-PL')}
        </time>}
      </div>
    </footer>
  </div>;
};

export const DashboardButtonsComponent = () => {
  const navigate = useNavigate();
  const isActive = useLocation().pathname === '/app/dashboard';
  const {theme} = useContext(ThemeContext);
  return <div onClick={() => navigate('/app/dashboard')}
    aria-current={isActive ? 'page' : undefined}
    style={{cursor: 'pointer', borderRadius: '30%', padding: '5px', backgroundColor: isActive ? 'var(--active-item-bg-color)' : 'transparent'}}
    role="button" tabIndex={0} aria-label="Przejdź do strony głównej"
    title="Przejdź do strony głównej">
    <FaHome style={{color: isActive ? 'var(--active-item-text-color)' : (theme === 'dark' ? 'var(--icon-color-dark)' : 'var(--menu-icon-color)')}}/>
  </div>;
};

export default DashboardComponent;
